import { DefaultTheme, DefaultTypography } from "styled-components/native";
import { Platform } from "react-native";
import { Shadow } from "./customization";

const deepFind = (obj: any, path: any) => {
  let paths = path.split("."),
    current = obj,
    i;

  for (i = 0; i < paths.length; ++i) {
    if (current[paths[i]] === undefined) {
      return undefined;
    } else {
      current = current[paths[i]];
    }
  }
  return current;
};

const parseKey = (subValue: string) => {
  let newKey: string | null = subValue;
  switch (subValue) {
    case "textDecoration":
      newKey = "textDecorationLine";
      break;

    case "textCase":
    case "paragraphIndent":
    case "paragraphSpacing":
      newKey = null;
      break;

    default:
      newKey = subValue;
      break;
  }
  return newKey;
};

const parseValue = (key: string, value: string) => {
  let parsedValue: any = value;
  switch (key) {
    case "fontWeight":
      parsedValue = value.toLowerCase();
      break;
    case "lineHeight":
    case "fontSize":
    case "letterSpacing":
    case "paragraphSpacing":
      // @ts-ignore
      parsedValue = isNaN(value) ? value : Number(value);
      break;
    default:
      parsedValue = value;
      break;
  }
  return parsedValue;
};

const generateThemeTokens = (
  currentToken: any,
  globalToken: any,
  theme: any
): DefaultTheme => {
  Object.entries(currentToken).forEach((value) => {
    // @ts-ignore
    if (typeof value[1] === "object" && !!value[1]?.type) {
      // @ts-ignore
      const tokenType = value[1]?.type;
      // @ts-ignore
      const tokenValue = value[1]?.value;

      // Handle color tokens (solid colors and gradients)
      if (tokenType === "color") {
        if (typeof tokenValue === "string" && tokenValue.startsWith("{")) {
          const unWraped = tokenValue.replace("{", "").replace("}", "");
          theme[value[0]] = deepFind(globalToken, unWraped)?.value;
        } else {
          theme[value[0]] = tokenValue;
        }
      }
      // Handle gradient tokens explicitly defined with type "gradient"
      else if (tokenType === "gradient") {
        if (typeof tokenValue === "string" && tokenValue.startsWith("{")) {
          const unWraped = tokenValue.replace("{", "").replace("}", "");
          theme[value[0]] = deepFind(globalToken, unWraped)?.value;
        } else {
          theme[value[0]] = tokenValue;
        }
      }
    } else {
      generateThemeTokens(value[1], globalToken, theme);
    }
  });

  return {
    transparent: "transparent",
    ...theme,
  };
};

export const hexToRgba = (hex: string) => {
  // Remove the '#' if it exists
  hex = hex.replace("#", "");

  // Extract RGB values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Extract alpha if available, default to 1
  const a =
    hex.length === 8 ? (parseInt(hex.substring(6, 8), 16) / 255).toFixed(2) : 1;

  return `${r}, ${g}, ${b}, ${a}`;
};

const EMPTY_SHADOW: Shadow = {
  x: 0,
  y: 0,
  blur: 0,
  spread: 0,
  color: "transparent",
};

export const DEFAULT_SHADOWS = {
  Normal: { ...EMPTY_SHADOW },
  Gentle: { ...EMPTY_SHADOW },
  Huge: { ...EMPTY_SHADOW },
  "Bottom-scroll": { ...EMPTY_SHADOW },
};

export const extractBoxShadows = (json: any, theme: any) => {
  const shadows = json?.global?.Shadows;
  if (!shadows) {
    return {
      ...theme,
      Shadows: theme.Shadows ?? DEFAULT_SHADOWS,
    };
  }

  const shadowObj: any = {
    ...DEFAULT_SHADOWS,
    ...theme.Shadows,
  };
  Object.keys(shadows).forEach((key) => {
    const shadow = shadows[key];
    if (shadow.type === "boxShadow") {
      shadowObj[key] = shadow.value as Shadow;
    }
  });
  theme.Shadows = shadowObj;
  return { ...theme };
};

const generateTypographyTokens = (
  currentToken: any,
  globalToken: any,
  typography: any
): DefaultTypography => {
  Object.entries(currentToken).forEach((value) => {
    // @ts-ignore
    if (typeof value[1] === "object" && !!value[1]?.type) {
      // @ts-ignore
      if (value[1]?.type === "typography") {
        // @ts-ignore
        if (typeof value[1]?.value === "object") {
          let valueObject: any = {};
          // @ts-ignore
          Object.entries(value[1]?.value).forEach((subValue) => {
            const key = parseKey(subValue[0]);
            if (key && key !== "lineHeight") {
              // @ts-ignore
              if (subValue[1].startsWith("{")) {
                // @ts-ignore
                const unWraped = subValue[1].replace("{", "").replace("}", "");
                valueObject[key] = parseValue(
                  key,
                  deepFind(globalToken, unWraped)?.value
                );
              } else {
                // @ts-ignore
                valueObject[key] = parseValue(key, subValue[1]);
              }
            }
          });
          typography[value[0]] = valueObject;
        }
      }
    } else {
      generateTypographyTokens(value[1], globalToken, typography);
    }
  });
  return typography;
};

const regenerateTypographyTokens = (typography: DefaultTypography) => {
  let generatedTypo = {};
  Object.entries(typography).forEach((value): any => {
    let generatedSubTypo = {};
    // @ts-ignore
    Object.entries(value[1]).forEach((subValue): any => {
      // @ts-ignore
      if (subValue[0] === "fontFamily" && value[1].fontWeight) {
        let subFont =
          value[1].fontWeight[0]?.toUpperCase() + value[1].fontWeight.slice(1);

        if (isSafariOrFirefox() && subFont === "Semibold") {
          subFont = "Regular";
        }

        // @ts-ignore
        generatedSubTypo[subValue[0]] = `${subValue[1]}-${subFont}`;
      } else if (subValue[0] === "fontWeight") {
        // @ts-ignore
        generatedSubTypo[subValue[0]] = convertFontWeight(subValue[1]);
      } else {
        // @ts-ignore
        generatedSubTypo[subValue[0]] = subValue[1];
      }
    });
    // @ts-ignore
    generatedTypo[value[0]] = generatedSubTypo;
  });
  return generatedTypo;
};

const convertFontWeight = (weight: string) => {
  let fontWeight = "400";
  switch (weight.toLowerCase()) {
    case "thin":
      fontWeight = "100";
      break;
    case "extralight":
      fontWeight = "200";
      break;
    case "light":
      fontWeight = "300";
      break;
    case "regular":
      fontWeight = "400";
      break;
    case "medium":
      fontWeight = "500";
      break;
    case "semibold":
      fontWeight = "600";
      break;
    case "bold":
      fontWeight = "700";
      break;
    case "extrabold":
      fontWeight = "800";
      break;
    case "black":
      fontWeight = "900";
      break;
    default:
      fontWeight = "400";
      break;
  }
  return fontWeight;
};

const generateTranslation = (obj: any) => {
  let parsedObj: any = {};
  Object.entries(obj).forEach((value: any) => {
    if (value[1].startsWith("{")) {
      const unWraped = value[1].replace("{", "").replace("}", "");
      parsedObj[value[0]] = obj[unWraped];
    } else parsedObj[value[0]] = value[1];
  });
  return parsedObj;
};

const generateTranslationObject = (globalObj: any, currentObj: any) => {
  let parsedObj: any = {};
  Object.entries(currentObj).forEach((value: any) => {
    if (typeof value[1] === "object") {
      parsedObj[value[0]] = generateTranslationObject(globalObj, value[1]);
    } else if (value[1].startsWith("{")) {
      const unWraped = value[1].replace("{", "").replace("}", "");
      parsedObj[value[0]] = deepFind(globalObj, unWraped);
    } else parsedObj[value[0]] = value[1];
  });
  return parsedObj;
};

const isSafariOrFirefox = () => {
  if (Platform.OS === "web") {
    if (navigator?.userAgent) {
      const userAgent = navigator.userAgent.toLowerCase();
      const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);
      const isFirefox = userAgent.indexOf("firefox") > -1;

      return isSafari || isFirefox;
    }

    return false;
  }

  return false;
};

const isSafari = () => {
  if (Platform.OS === "web") {
    if (navigator?.userAgent) {
      const userAgent = navigator.userAgent.toLowerCase();
      const flag = /^((?!chrome|android).)*safari/i.test(userAgent);
      return flag;
    }
    return false;
  }
  return false;
};

const isIPhone = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  return /iPhone|iPod/.test(userAgent) && /Safari/.test(userAgent);
};

const isIpad = () => {
  // const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  // return (
  //   /iPad/.test(userAgent) ||
  //   (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  // );
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  return (
    /iPad/.test(userAgent) ||
    (/\bMac/.test(userAgent) &&
      navigator.maxTouchPoints > 0 &&
      !/iPhone/.test(userAgent))
  );
};

export {
  generateThemeTokens,
  generateTypographyTokens,
  regenerateTypographyTokens,
  deepFind,
  generateTranslation,
  generateTranslationObject,
  isSafari,
  isIpad,
  isIPhone,
};
